import {announce} from '@github-ui/aria-live'
import {LABELS} from '@github-ui/issues-shared/labels'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {Spinner} from '@primer/react'
import {type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef} from 'react'

import {useDeleteSavedViewDialog} from '../../hooks/use-delete-saved-view-dialog'
import {pickPostDeleteFocusTargetId} from './pick-post-delete-focus-target'
import type {PinControls} from './PinViewMenuItem'
import styles from './RepositoryViews.module.css'
import {resolvePostDeleteFocusTarget} from './resolve-post-delete-focus-target'
import type {SavedView} from './types'
import {ViewRow} from './ViewRow'
import type {ViewsSortValue} from './views-sort-options'
import {ViewsSortMenu} from './ViewsSortMenu'

export type ViewsListSection = {
  title: string
  isLastView: (deletedId: SavedView['id']) => boolean
  otherSectionsHaveViews: boolean
  headingRef: RefObject<HTMLElement | null>
}

type ViewsListProps = {
  views: SavedView[]
  owner: string
  repo: string
  sort: ViewsSortValue
  onSortChange: (value: ViewsSortValue) => void
  isLoading?: boolean
  emptyState?: ReactNode
  viewerCanManageViews?: boolean
  showPinControls?: boolean
  pinControls?: PinControls
  title?: string
  ariaLabelledBy?: string
  hideMetadata?: boolean
  section?: ViewsListSection
}

export function ViewsList({
  views,
  owner,
  repo,
  sort,
  onSortChange,
  isLoading = false,
  emptyState,
  viewerCanManageViews = false,
  showPinControls = false,
  pinControls,
  title = LABELS.views.viewsListTitle,
  ariaLabelledBy,
  hideMetadata = false,
  section,
}: ViewsListProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const deleteReturnFocusRef = useRef<HTMLElement | null>(null)
  const emptyFocusFallbackRef = useRef<HTMLElement | null>(null)
  const shouldFocusFallbackAfterDeleteRef = useRef(false)

  const handleDeleted = useCallback(
    (deletedView: SavedView) => {
      const target = pickPostDeleteFocusTargetId(views, deletedView.id) ?? 'heading'
      const noRowsLeft = target === 'heading'
      const survivingSection = section?.otherSectionsHaveViews ? section : undefined

      if (noRowsLeft) {
        deleteReturnFocusRef.current = null
        emptyFocusFallbackRef.current = survivingSection?.headingRef.current ?? null
        shouldFocusFallbackAfterDeleteRef.current = true
      } else {
        deleteReturnFocusRef.current = resolvePostDeleteFocusTarget(target, containerRef.current)
        shouldFocusFallbackAfterDeleteRef.current = false
      }

      const emptiedList = noRowsLeft && (section?.isLastView(deletedView.id) ?? true)
      const emptiedSectionTitle = survivingSection?.title
      announce(
        !emptiedList
          ? LABELS.views.deleteSuccess(deletedView.name)
          : emptiedSectionTitle
            ? LABELS.views.deleteSuccessLastSectionView(deletedView.name, emptiedSectionTitle)
            : LABELS.views.deleteSuccessLastView(deletedView.name),
        {assertive: true},
      )
    },
    [section, views],
  )

  const {openDeleteDialog, deleteDialog, isDeleteDialogOpen} = useDeleteSavedViewDialog({
    owner,
    repo,
    onDeleted: handleDeleted,
    returnFocusRef: deleteReturnFocusRef,
  })

  useEffect(() => {
    if (isDeleteDialogOpen || !shouldFocusFallbackAfterDeleteRef.current) return

    shouldFocusFallbackAfterDeleteRef.current = false
    resolvePostDeleteFocusTarget('heading', null, emptyFocusFallbackRef.current)?.focus()
    emptyFocusFallbackRef.current = null
  }, [isDeleteDialogOpen])

  const actions = useMemo(
    () => [
      {
        key: 'views-sort-menu',
        render: () => <ViewsSortMenu sort={sort} onSortChange={onSortChange} />,
      },
    ],
    [sort, onSortChange],
  )

  const itemCount = isLoading ? undefined : LABELS.views.itemCount(views.length)
  const metadata = hideMetadata ? undefined : (
    <ListViewMetadata
      title={itemCount}
      actions={actions}
      actionsLabel={LABELS.views.actionsLabel}
      assistiveAnnouncement={isLoading ? LABELS.views.loadingSavedViews : undefined}
    />
  )

  return (
    <div ref={containerRef}>
      <ListView
        title={title}
        ariaLabelledBy={ariaLabelledBy}
        metadata={metadata}
        titleHeaderTag="h2"
        emptyState={emptyState}
      >
        {isLoading ? (
          <li className={styles.loadingContainer}>
            <Spinner size="large" srText={null} />
          </li>
        ) : (
          views.map(view => (
            <ViewRow
              key={view.id}
              view={view}
              owner={owner}
              repo={repo}
              canManage={viewerCanManageViews}
              onRequestDelete={openDeleteDialog}
              showPinnedBadge={showPinControls && Boolean(view.pinned)}
              pinControls={pinControls}
            />
          ))
        )}
      </ListView>
      {deleteDialog}
    </div>
  )
}
